import { useEffect, useState } from "react";
import { ClipboardCheck } from "lucide-react";
import { db } from "@/lib/db";
import type { ReviewItem, Document } from "@/types";

export default function ReviewQueue() {
  const [items, setItems] = useState<(ReviewItem & { document?: Document })[]>([]);

  const load = async () => {
    const reviewItems = await db.reviewItems.where({ status: "pending" }).toArray();
    const docs = await db.documents.bulkGet(reviewItems.map((r) => r.documentId));
    setItems(
      reviewItems.map((r, i) => ({
        ...r,
        document: docs[i] || undefined,
      }))
    );
  };

  useEffect(() => {
    load();
  }, []);

  const handleAction = async (id: string, status: "accepted" | "dismissed") => {
    await db.reviewItems.update(id, { status, updatedAt: new Date().toISOString() });
    load();
  };

  return (
    <div className="space-y-5">
      <h3 className="text-sm font-semibold text-text-primary dark:text-dark-text-primary">
        Review Queue ({items.length})
      </h3>

      {items.length === 0 ? (
        <div className="rounded-xl border border-border-default bg-surface-0 p-8 text-center shadow-sm dark:border-dark-border-default dark:bg-dark-surface-1">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-surface-2 dark:bg-dark-surface-2 mb-3">
            <ClipboardCheck size={24} className="text-text-muted" />
          </div>
          <p className="text-sm font-medium text-text-secondary dark:text-dark-text-secondary">No items to review</p>
          <p className="mt-1 text-xs text-text-muted">All caught up — check back after your next sync</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between rounded-xl border border-border-default bg-surface-0 p-4 shadow-sm dark:border-dark-border-default dark:bg-dark-surface-1"
            >
              <div>
                <p className="text-sm font-medium text-text-primary dark:text-dark-text-primary">
                  {item.document?.canonicalName || "Unknown document"}
                </p>
                <p className="text-xs text-text-muted dark:text-dark-text-muted capitalize">{item.reason.replace(/_/g, " ")}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleAction(item.id, "accepted")}
                  className="rounded-lg bg-success px-3 py-1.5 text-xs font-medium text-white transition-colors hover:opacity-90"
                >
                  Accept
                </button>
                <button
                  onClick={() => handleAction(item.id, "dismissed")}
                  className="rounded-lg border border-border-strong bg-surface-0 px-3 py-1.5 text-xs font-medium text-text-secondary transition-colors hover:bg-surface-2 dark:border-dark-border-strong dark:bg-dark-surface-1 dark:text-dark-text-secondary dark:hover:bg-dark-surface-2"
                >
                  Dismiss
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
